import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import Socials from "containers/public/Profile/Body/About/Socials";
import Links from "containers/public/Profile/Body/About/Links";
import FloatingAlert from "./FloatingAlert";

import ProfileImages from "ui/ProfileImages";
import Description from "ui/text/Description";
import Title from "ui/text/Title";

import { darkTheme, defaultTheme } from "utils/themes";

import "./styles.css";

const Preview = ({ originalOrganizationSlug, organization, socials, links }) => {
  const [showFloatingAlert, setShowFloatingAlert] = useState(true);
  const previewRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setShowFloatingAlert(false);
    };

    const previewElement = previewRef.current;
    if (previewElement) {
      previewElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (previewElement) {
        previewElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const {
    title,
    highlight_color = "#F75201",
    custom_cta,
    description,
    theme,
    logo_file,
    logo_url,
    cover_image_file,
    cover_image_url,
    preferences,
    followers,
  } = organization;

  const coverImageSrc = cover_image_file ? URL.createObjectURL(cover_image_file) : cover_image_url;
  const imageSrc = logo_file ? URL.createObjectURL(logo_file) : logo_url;

  const selectedTheme = theme === "dark" ? darkTheme : defaultTheme;

  const { font_family,
          primary_text_color,
          secondary_text_color,
          primary_button_color,
          secondary_button_color,
          button_text_color,
          primary_background_color,
          secondary_background_color,
          border_color,
          stripe_theme
        } = selectedTheme;

  const showMemberCount = !preferences?.["hide_members_count"];
  const showCta = !preferences?.["hide_cta"];
  const showSocialLinks = !preferences?.["hide_social_links"];
  const showDescription = !preferences?.["hide_description"];

  const showContentContainer = showDescription || showSocialLinks || links.length > 0;

  return (
    <div className="profile-preview_v2_container">
      <div
        ref={previewRef}
        className="profile-preview_v2"
        style={{
          backgroundColor: primary_background_color,
          '--fontFamily': `'${font_family}', sans-serif`,
          '--highlightColor': highlight_color,
          '--primaryTextColor': primary_text_color,
          '--secondaryTextColor': secondary_text_color,
          '--primaryButtonColor': primary_button_color,
          '--secondaryButtonColor': secondary_button_color,
          '--buttonTextColor': button_text_color,
          '--primaryBackgroundColor': primary_background_color,
          '--secondaryBackgroundColor': secondary_background_color,
          '--borderColor': border_color,
          '--stripeTheme': stripe_theme,
        }}
      >
        <ProfileImages
          coverImageSrc={coverImageSrc}
          profileImageSrc={imageSrc}
        />

        <div className="content" >
          <div className="header" style={{ marginBottom: showContentContainer || showCta ? 0 : 16 }}>
            <Title bold>{title}</Title>
            {showMemberCount &&
              <Description secondary>{followers + " members"}</Description>
            }
          </div>

          {showCta &&
            <button
              className="ui-button highlighted"
              style={{ backgroundColor: highlight_color, borderColor: highlight_color, marginBottom: showContentContainer ? 0 : 16 }}
              onClick={(e) => e.preventDefault()}
            >
              {custom_cta || "Join the club"}
            </button>
          }

          {showContentContainer && (
            <div className="content-container" style={{ backgroundColor: secondary_background_color }}>
              <div className="description-container">
                {showDescription && (
                  <Description>
                    {description}
                  </Description>
                )}
              </div>

              {showSocialLinks && (
                <Socials socials={socials} organizationSlug={originalOrganizationSlug} preview={true}/>
              )}

              <Links links={links} organizationSlug={originalOrganizationSlug} preview={true}/>
            </div>
          )}
        </div>
      </div>

      {links.length > 1 && (
        <FloatingAlert show={showFloatingAlert} />
      )}
    </div>
  );
};

Preview.propTypes = {
  originalOrganizationSlug: PropTypes.string,
  organization: PropTypes.object,
  socials: PropTypes.array,
  links: PropTypes.array,
};

export default Preview;
